import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {number|null} compare_at_price
 * @property {boolean} available
 * @property {string[]} options
 * @property {SellingPlanAllocation[]} [selling_plan_allocations]
 */

/**
 * @typedef {Object} SellingPlanAllocation
 * @property {number} selling_plan_id
 * @property {number} per_delivery_price
 * @property {number|null} compare_at_price
 */

/**
 * @typedef {Object} AromelyPdpRefs
 * @property {HTMLInputElement} variantIdInput
 * @property {HTMLInputElement} sellingPlanInput
 * @property {HTMLElement} priceDisplay
 * @property {HTMLElement} [comparePriceDisplay]
 * @property {HTMLElement} [discountBadge]
 * @property {HTMLButtonElement} submitBtn
 * @property {HTMLElement} submitBtnLabel
 * @property {HTMLFormElement} productForm
 */

/**
 * Custom PDP component handling variant selection, selling plans, and add-to-cart.
 * @extends {Component<AromelyPdpRefs>}
 */
class AromelyPdp extends Component {
  /** @type {Variant[]} */
  #variants = [];

  /** @type {string} */
  #moneyFormat = '';

  /** @type {string} */
  #currency = '';

  /** @type {string} */
  #tAddToCartFor = '';

  /** @type {string} */
  #tSoldOut = '';

  /** @type {AbortController} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const fitQuantity = this.querySelector('[data-fit-pdp-quantity]');
    const requestedQuantity = new URL(window.location.href).searchParams.get('quantity');
    if (fitQuantity && /^[1-4]$/.test(requestedQuantity || '')) {
      const select = fitQuantity.querySelector('select');
      if (!select.dataset.initialized) {
        select.value = requestedQuantity;
        select.dataset.initialized = 'true';
      }
      select.disabled = false;
      fitQuantity.hidden = false;
      select.addEventListener('change', () => {
        const variant = this.#variants.find(v => String(v.id) === this.refs.variantIdInput?.value);
        if (variant) this.#updatePriceDisplay(variant);
      }, { signal });
    }

    // Parse variants data
    const variantsScript = this.querySelector('[data-variants-json]');
    if (variantsScript) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent || '[]');
      } catch { /* empty */ }
    }

    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#currency = this.dataset.currency || 'USD';
    this.#tAddToCartFor = this.dataset.tAddToCartFor || 'Add to cart for __PRICE__';
    this.#tSoldOut = this.dataset.tSoldOut || 'Sold out';

    // Variant radio change — each option group has its own name="option-{handle}"
    this.querySelectorAll('input[type="radio"][data-option-index]').forEach((radio) => {
      radio.addEventListener('change', this.#onVariantChange, { signal });
    });

    // Selling plan radio change
    this.querySelectorAll('input[name="selling-plan-option"]').forEach((radio) => {
      radio.addEventListener('change', this.#onSellingPlanChange, { signal });
    });

    // Sync hidden selling_plan input from the initially checked radio
    const checkedPlan = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="selling-plan-option"]:checked')
    );
    if (checkedPlan && this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = checkedPlan.value;
    }

    // Refresh the price display to reflect any pre-selected selling plan.
    // The server-rendered price block shows the variant's base price, so if
    // a subscription pill is checked by default the displayed price would
    // otherwise stay stuck on the base price until the user interacts.
    const initialVariantId = this.refs.variantIdInput?.value;
    const initialVariant = this.#variants.find((v) => v.id.toString() === initialVariantId);
    if (initialVariant) {
      this.#updatePriceDisplay(initialVariant);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Handles add-to-cart via fetch POST.
   * @param {Event} event
   */
  /** @type {number|undefined} */
  #successTimeout;

  handleSubmit(event) {
    event.preventDefault();

    const form = this.refs.productForm;
    if (!form) return;

    const btn = this.refs.submitBtn;
    if (!btn || btn.disabled) return;
    if (btn.dataset.state === 'loading') return;

    // Show loading state
    btn.dataset.state = 'loading';
    btn.disabled = true;

    const formData = new FormData(form);

    // Remove selling_plan if empty (one-time purchase)
    if (!formData.get('selling_plan')) {
      formData.delete('selling_plan');
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('Add to cart error:', response.description);
          btn.dataset.state = '';
          btn.disabled = false;
          return;
        }

        // Show success checkmark
        btn.dataset.state = 'success';

        this.dispatchEvent(
          new CartAddEvent(response, this.id, {
            source: 'aromely-pdp',
            productId: this.dataset.productId,
            variantId: formData.get('id')?.toString(),
          })
        );

        // Reset after delay
        if (this.#successTimeout) clearTimeout(this.#successTimeout);
        this.#successTimeout = setTimeout(() => {
          btn.dataset.state = '';
          btn.disabled = false;
        }, 1500);
      })
      .catch((error) => {
        console.error('Add to cart error:', error);
        btn.dataset.state = '';
        btn.disabled = false;
      });
  }

  /**
   * When a variant option (size) is selected, find the matching variant
   * and update the hidden input + price display.
   */
  #onVariantChange = (e) => {
    // Collect selected option values from each option group
    const selectedOptions = [];
    const optionGroups = this.querySelectorAll('[data-option-group]');

    if (optionGroups.length > 0) {
      // Multi-option: resolve variant from combination of selected values
      for (const group of optionGroups) {
        const checked = /** @type {HTMLInputElement|null} */ (
          group.querySelector('input[type="radio"]:checked')
        );
        if (checked) {
          selectedOptions[parseInt(checked.dataset.optionIndex, 10)] = checked.dataset.optionValue;
        }
      }

      // Find variant matching all selected options
      const variant = this.#variants.find((v) =>
        v.options.every((opt, i) => selectedOptions[i] === undefined || opt === selectedOptions[i])
      );
      if (!variant) return;

      this.#applyVariantChange(variant, e?.target);
    } else {
      // Single-option fallback: use the radio value directly as variant ID
      const selected = /** @type {HTMLInputElement|null} */ (e?.target);
      if (!selected) return;

      const variant = this.#variants.find((v) => v.id.toString() === selected.value);
      if (!variant) return;

      this.#applyVariantChange(variant, selected);
    }
  };

  /**
   * Apply variant change: update form, URL, labels, price, and dispatch event.
   * @param {Variant} variant
   * @param {HTMLInputElement} [selectedInput]
   */
  #applyVariantChange(variant, selectedInput) {
    const variantId = variant.id.toString();

    // Update hidden variant input
    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = variantId;
    }

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());

    // Update swatch value label
    if (selectedInput) {
      const swatchLabel = selectedInput.getAttribute('aria-label');
      if (swatchLabel) {
        const optionContainer = selectedInput.closest('[data-option-group]') ||
          selectedInput.closest('.flex.flex-col.gap-2');
        const valueSpan = optionContainer?.querySelector('[data-swatch-value]');
        if (valueSpan) valueSpan.textContent = swatchLabel;
      }
    }

    // Update price and availability
    this.#updatePriceDisplay(variant);

    // Dispatch custom event for other components (kit, gallery, etc.)
    this.dispatchEvent(new CustomEvent('aromely:variant-change', {
      bubbles: true,
      detail: { variantId: variant.id, price: variant.price, variant },
    }));
  }

  /**
   * When a selling plan option changes, update the hidden input + price.
   */
  #onSellingPlanChange = () => {
    const selected = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="selling-plan-option"]:checked')
    );
    if (!selected) return;

    const sellingPlanId = selected.value;

    // Update hidden selling plan input
    if (this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = sellingPlanId;
    }

    // Re-render price with the new selling plan context
    const currentVariantId = this.refs.variantIdInput?.value;
    const variant = this.#variants.find((v) => v.id.toString() === currentVariantId);
    if (variant) {
      this.#updatePriceDisplay(variant);
    }
  };

  /**
   * Repaint the price line for the current variant, selling plan and collection
   * promotion.
   *
   * Mirrors snippets/aromely-price.liquid exactly — same order, same integer
   * cents, same truncation — so the server paint and every client update agree
   * for a given variant/plan/promotion. Change one, change the other.
   *
   *   1. Resolve the real one-time price (`variant.price`).
   *   2. Resolve the plan price from Shopify's own price adjustment, if a plan
   *      is selected. That plan price strikes the one-time price.
   *   3. Apply the collection promotion to whichever of those is active. It
   *      strikes the price it came off and takes over the badge.
   *
   * `variant.compare_at_price` is deliberately never read: it is a catalogue
   * merchandising value, not a price a shopper was ever charged, so striking it
   * advertised a saving the store does not give.
   *
   * Plan prices come from `data-adjust-*`, sourced from
   * `selling_plan.price_adjustments` in blocks/aromely-pdp-subscribe.liquid,
   * because `{{ product.variants | json }}` omits selling_plan_allocations.
   * Nothing here hard-codes a plan percentage.
   *
   * @param {Variant} variant
   */
  #updatePriceDisplay(variant) {
    const sellingPlanId = this.refs.sellingPlanInput?.value;
    const hasSellingPlan = !!sellingPlanId;

    let basePrice = variant.price;
    let planCompare = 0;

    if (hasSellingPlan) {
      const checkedPlan = this.querySelector(`input[name="selling-plan-option"][value="${sellingPlanId}"]`);
      if (checkedPlan instanceof HTMLElement) {
        const adjustType = checkedPlan.dataset.adjustType;
        const adjustValue = Number.parseFloat(checkedPlan.dataset.adjustValue || '0');

        if (adjustType === 'percentage' && adjustValue > 0) {
          basePrice = Math.round((variant.price * (100 - adjustValue)) / 100);
        } else if (adjustType === 'fixed_amount' && adjustValue > 0) {
          basePrice = variant.price - adjustValue;
        } else if (adjustType === 'price' && adjustValue > 0) {
          basePrice = adjustValue;
        }

        if (basePrice < variant.price) planCompare = variant.price;
      }
    }

    const promoPct = this.#collectionPromoPct();

    // Strike the reference price the shopper could pay instead — the real
    // one-time price when a plan undercuts it — then derive the badge from the
    // two figures actually printed either side of it. A subscribed line under a
    // promotion therefore reports the COMBINED saving off the one-time price,
    // matching the card that linked here and the cart line it becomes.
    const referencePrice = planCompare > basePrice ? planCompare : basePrice;

    let displayedPrice = basePrice;
    if (promoPct > 0) {
      // Truncate, as Shopify does to a line-level discount amount.
      displayedPrice = basePrice - Math.floor((basePrice * promoPct) / 100);
    }

    let displayedCompare = 0;
    let badgePct = 0;
    if (referencePrice > displayedPrice) {
      displayedCompare = referencePrice;
      badgePct = Math.round(((referencePrice - displayedPrice) * 100) / referencePrice);
    }
    // Not translated: "-15%" reads the same in every storefront locale.
    const badgeLabel = `-${badgePct}%`;

    const onSale = displayedCompare > displayedPrice;
    const formattedPrice = formatMoney(displayedPrice, this.#moneyFormat, this.#currency);

    if (this.refs.priceDisplay) {
      this.refs.priceDisplay.textContent = formattedPrice;
    }

    if (this.refs.comparePriceDisplay) {
      if (onSale) {
        this.refs.comparePriceDisplay.textContent = formatMoney(displayedCompare, this.#moneyFormat, this.#currency);
      }
      this.refs.comparePriceDisplay.classList.toggle('hidden', !onSale);
    }

    if (this.refs.discountBadge) {
      if (onSale) this.refs.discountBadge.textContent = badgeLabel;
      // .inline-flex and .hidden have equal specificity, so the badge must
      // never carry both — it would paint as an empty pill.
      this.refs.discountBadge.classList.toggle('inline-flex', onSale);
      this.refs.discountBadge.classList.toggle('hidden', !onSale);
    }

    const quantitySelect = this.querySelector('[data-fit-pdp-quantity] select:not(:disabled)');
    const quantity = quantitySelect ? Number(quantitySelect.value) : 1;
    this.#updateButtonLabel(variant, formatMoney(displayedPrice * quantity, this.#moneyFormat, this.#currency), hasSellingPlan);
  }

  /**
   * The collection promotion percentage server-resolved onto the price line by
   * snippets/aromely-collection-discount.liquid. Only a whole 1-100 counts;
   * anything else means no promotion.
   * @returns {number}
   */
  #collectionPromoPct() {
    const container = this.refs.priceDisplay?.closest('[data-collection-promo]');
    const raw = Number.parseFloat(container?.getAttribute('data-collection-promo') || '0');
    if (!Number.isFinite(raw) || raw < 1 || raw > 100 || !Number.isInteger(raw)) return 0;
    return raw;
  }

  /**
   * Update add-to-cart button label and availability.
   * @param {Variant} variant
   * @param {string} formattedPrice
   * @param {boolean} hasSellingPlan
   */
  #updateButtonLabel(variant, formattedPrice, hasSellingPlan) {
    const btn = this.refs.submitBtn;
    if (!btn) return;

    if (variant.available) {
      btn.disabled = false;
      btn.dataset.state = '';
      if (this.refs.submitBtnLabel) {

        this.refs.submitBtnLabel.textContent = this.#tAddToCartFor.replace('__PRICE__', formattedPrice);
      }
    } else {
      btn.disabled = true;
      btn.dataset.state = '';
      if (this.refs.submitBtnLabel) {
        this.refs.submitBtnLabel.textContent = this.#tSoldOut;
      }
    }
  }

}

if (!customElements.get('aromely-pdp')) {
  customElements.define('aromely-pdp', AromelyPdp);
}
